"use client";
import { jsx, jsxs } from "react/jsx-runtime";
import { useFormik } from "formik";
import { isBD } from "../../../../utils/businessType.lib.js";
import { DELIVERY_LOCATION_LOCALE_VALIDATION_CONFIGURATION, createIMask, validateDeliveryLocation } from "../../../../utils/validations/deliveryLocations.js";
import { useRef, useState } from "react";
import { Notification, TextField, Button } from "@nxt-costco-com/forge-components";
import { SpaceXs, SpaceSm } from "@nxt-costco-com/forge-design-tokens";
import { Analytics } from "../../../Analytics/index.js";
import { replaceStringTemplate } from "./lib.js";
const DeliveryLocationForm = (props) => {
  const inputRef = useRef(null);
  const {
    agent,
    data,
    isBDPage: isBDPageProp,
    onClose,
    onSubmit,
    tmpZipCode,
    locale,
    skipValidation
  } = props;
  const {
    inputTitle,
    buttonLabel,
    invalidInputErrorMessage,
    serviceDownErrorMessage = `Sorry, we can't update your delivery location right now. Please try again later.`,
    deliveryNotAvailable,
    enterFullDeliveryAddress,
    deliveryToLimitedAreas,
    validationSchemaType = "en-us"
  } = data || {};
  const validationConfiguration = DELIVERY_LOCATION_LOCALE_VALIDATION_CONFIGURATION[validationSchemaType];
  const validationSchema = validationConfiguration == null ? void 0 : validationConfiguration.validationSchema;
  const maxCharacterLimit = validationConfiguration == null ? void 0 : validationConfiguration.maxCharacterLimit;
  const inputMaskConfiguration = validationConfiguration == null ? void 0 : validationConfiguration.inputMaskConfiguration;
  const inputMask = createIMask(inputMaskConfiguration);
  const initialValues = { city: "", state: "", zipCode: tmpZipCode || "" };
  const [errorMessage, setErrorMessage] = useState("");
  const [tmpErrorConfig, setTmpErrorConfig] = useState();
  const isBDPage = isBDPageProp || isBD();
  const formik = useFormik({
    initialValues,
    onSubmit: async (values) => {
      let warehouse, country, city, state, deliveryMethodsConfig, confirmationConfig, validationErrorConfig;
      if (!skipValidation) {
        const response = await validateDeliveryLocation(
          agent,
          validationSchema,
          values,
          {
            isBDPage,
            onlyValidate: true
          },
          locale
        );
        if (!response) {
          return;
        }
        ({
          warehouse,
          country,
          city,
          state,
          errorConfig: validationErrorConfig,
          deliveryMethodsConfig,
          confirmationConfig
        } = response);
        setTmpErrorConfig(validationErrorConfig);
      }
      if (onSubmit) {
        const response = await onSubmit({
          ...values,
          city: city || "",
          state: state || "",
          warehouse: warehouse || "",
          country: country || "",
          errorConfig: validationErrorConfig,
          ...!!confirmationConfig && { confirmationConfig },
          // don't include this unless it's returned by the API; should only be used on BD
          ...!!deliveryMethodsConfig && { deliveryMethodsConfig }
        });
        const { errorConfig } = response || {};
        if (errorConfig) {
          setTmpErrorConfig(errorConfig);
          if ((errorConfig == null ? void 0 : errorConfig.type) === "server" && (errorConfig == null ? void 0 : errorConfig.message)) {
            setErrorMessage(errorConfig == null ? void 0 : errorConfig.message);
          } else if ((errorConfig == null ? void 0 : errorConfig.type) === "deliveryNotAvailable") {
            setErrorMessage(deliveryNotAvailable || "Unavailable");
          } else if ((errorConfig == null ? void 0 : errorConfig.type) === "deliveryToLimitedAreas") {
            setErrorMessage(
              replaceStringTemplate(
                deliveryToLimitedAreas || "",
                {
                  oldZip: `${values.zipCode}` || "{{oldZip}}"
                }
              ) || "Unavailable"
            );
          } else if ((errorConfig == null ? void 0 : errorConfig.type) === "serviceDownError") {
            setErrorMessage(serviceDownErrorMessage);
          } else {
            setTmpErrorConfig({
              type: "serviceDownError",
              severity: "general"
            });
            setErrorMessage(serviceDownErrorMessage);
          }
          return errorConfig;
        }
      }
    },
    validate: async (values) => {
      var _a;
      if (skipValidation) {
        return;
      }
      const response = await validateDeliveryLocation(
        agent,
        validationSchema,
        values,
        { isBDPage, onlyValidate: true },
        locale
      );
      if (!response) {
        setTmpErrorConfig({
          severity: "alert",
          type: "serviceDownError"
        });
        setErrorMessage(serviceDownErrorMessage);
        return;
      }
      const { errors, errorConfig } = response;
      if ((errorConfig == null ? void 0 : errorConfig.type) === "deliveryZipSame") {
        onClose();
        return errorConfig;
      }
      if (errors) {
        (_a = inputRef == null ? void 0 : inputRef.current) == null ? void 0 : _a.focus();
        return errors;
      }
      setTmpErrorConfig(errorConfig);
      if (errorConfig) {
        if ((errorConfig == null ? void 0 : errorConfig.type) === "server" && (errorConfig == null ? void 0 : errorConfig.message)) {
          setErrorMessage(errorConfig == null ? void 0 : errorConfig.message);
        } else if ((errorConfig == null ? void 0 : errorConfig.type) === "deliveryNotAvailable") {
          setErrorMessage(deliveryNotAvailable || "Unavailable");
        } else if ((errorConfig == null ? void 0 : errorConfig.type) === "deliveryToLimitedAreas") {
          setErrorMessage(
            replaceStringTemplate(deliveryToLimitedAreas || "", {
              oldZip: `${values.zipCode}` || "{{oldZip}}"
            }) || "Unavailable"
          );
        } else if ((errorConfig == null ? void 0 : errorConfig.type) === "serviceDownError") {
          setErrorMessage(serviceDownErrorMessage);
        }
        return errorConfig;
      }
    },
    validateOnBlur: false,
    validateOnChange: false
  });
  if (!data) return null;
  if (!validationConfiguration) {
    console.error(
      `Missing valid validationSchemaType: Options are ${Object.keys(DELIVERY_LOCATION_LOCALE_VALIDATION_CONFIGURATION).join(" | ")}`
    );
    return null;
  }
  const handleChange = (e) => {
    const formattedValue = inputMask.resolve(e.target.value);
    e.target.value = formattedValue;
    setTmpErrorConfig(void 0);
    return formik.handleChange(e);
  };
  const handleSubmit = (event) => {
    formik.validateForm().then(() => {
      event.preventDefault();
      formik.handleSubmit();
    });
  };
  const formattedFullDeliveryAddress = enterFullDeliveryAddress && replaceStringTemplate(enterFullDeliveryAddress, {
    submittedZip: `${formik.values.zipCode}` || "{{submittedZip}}"
  });
  const delimitedString = formattedFullDeliveryAddress == null ? void 0 : formattedFullDeliveryAddress.split("|");
  const FullDeliveryAddressRequiredMessage = () => {
    const handleAddressFormPage = () => {
      if (onSubmit) {
        onSubmit({
          ...formik.values,
          city: "",
          state: "",
          warehouse: "",
          country: "",
          errorConfig: tmpErrorConfig
        });
      }
    };
    const EnterFullDeliveryAddress = (delimitedText) => {
      return /* @__PURE__ */ jsx(
        Button,
        {
          variant: "text-button",
          onClick: handleAddressFormPage,
          sx: { textDecoration: "underline" },
          children: delimitedText
        }
      );
    };
    const sendDeliveryAddressMessageNode = [];
    sendDeliveryAddressMessageNode.push(delimitedString == null ? void 0 : delimitedString[0]);
    sendDeliveryAddressMessageNode.push(
      EnterFullDeliveryAddress((delimitedString == null ? void 0 : delimitedString[1]) || "")
    );
    sendDeliveryAddressMessageNode.push(delimitedString == null ? void 0 : delimitedString[2]);
    return /* @__PURE__ */ jsx(
      Notification,
      {
        severity: "alert",
        message: sendDeliveryAddressMessageNode.map((node, index) => /* @__PURE__ */ jsx("span", { children: node }, index))
      }
    );
  };
  return /* @__PURE__ */ jsx(
    Analytics,
    {
      analyticData: {
        component: {
          trackedData: [formik.values.zipCode]
        }
      },
      children: /* @__PURE__ */ jsxs("form", { "data-testid": "deliverylocationform", onSubmit: handleSubmit, children: [
        (tmpErrorConfig == null ? void 0 : tmpErrorConfig.type) === "enterFullDeliveryAddress" && delimitedString && /* @__PURE__ */ jsx(FullDeliveryAddressRequiredMessage, {}),
        tmpErrorConfig && errorMessage && (tmpErrorConfig == null ? void 0 : tmpErrorConfig.type) !== "enterFullDeliveryAddress" && /* @__PURE__ */ jsx(
          Notification,
          {
            message: errorMessage,
            severity: tmpErrorConfig.severity || "general",
            sx: { marginTop: SpaceSm, padding: SpaceXs }
          }
        ),
        /* @__PURE__ */ jsx(
          TextField,
          {
            errorText: formik.errors.zipCode ? invalidInputErrorMessage : void 0,
            sx: {
              marginBottom: 4,
              marginTop: 4
            },
            id: "zipCode",
            inputLabelId: inputTitle,
            inputRef,
            isError: !!formik.errors.zipCode,
            isRequired: true,
            label: inputTitle,
            maxCharacterLimit,
            name: "zipCode",
            onBlur: formik.handleBlur,
            onChange: handleChange,
            size: "small",
            type: "text",
            uniqueId: "deliverylocationform--zipcode",
            value: formik.values.zipCode
          }
        ),
        /* @__PURE__ */ jsx(
          Button,
          {
            sx: {
              width: "100%"
            },
            "data-testid": "deliverylocationform--submit",
            onClick: formik.handleSubmit,
            type: "submit",
            children: buttonLabel
          }
        )
      ] })
    }
  );
};
export {
  DeliveryLocationForm
};
